import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import KpiCard from "@/components/common/KpiCard";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Files, Upload, Download, Trash2, RotateCcw, Search, Share2, Eye, PlusCircle, ClipboardCheck, AlertTriangle } from "lucide-react";

export default function DocumentVaultPage() {
  const [tab, setTab] = useState("dashboard");
  return (
    <div className="animate-fade-up" data-testid="vault-page">
      <PageHeader overline="Coffre-fort" title="Documents"
        description="Coffre unique multi-lié (immeubles, lots, baux, locataires, crédits, projets). Versioning, partage sécurisé, corbeille, préparation OCR/IA." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="dashboard" data-testid="vault-tab-dashboard">Tableau de bord</TabsTrigger>
          <TabsTrigger value="list" data-testid="vault-tab-list">Documents</TabsTrigger>
          <TabsTrigger value="search" data-testid="vault-tab-search">Recherche</TabsTrigger>
          <TabsTrigger value="trash" data-testid="vault-tab-trash">Corbeille</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"><DashboardTab /></TabsContent>
        <TabsContent value="list"><ListTab /></TabsContent>
        <TabsContent value="search"><SearchTab /></TabsContent>
        <TabsContent value="trash"><TrashTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
function DashboardTab() {
  const [dash, setDash] = useState(null);
  useEffect(() => { api.get("/vault/dashboard/overview").then(({ data }) => setDash(data)); }, []);
  if (!dash) return <div className="text-muted-foreground">Chargement…</div>;
  const t = dash.totals;
  return (
    <div className="space-y-6">
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard name="v-total" label="Total documents" icon={Files} tone="accent" value={t.total_documents} />
        <KpiCard name="v-expired" label="Expirés" icon={AlertTriangle} value={t.expired} />
        <KpiCard name="v-missing" label="Manquants" icon={ClipboardCheck} value={t.missing_count} />
        <KpiCard name="v-trashed" label="Corbeille" icon={Trash2} value={t.trashed} />
      </section>
      {dash.alerts?.length > 0 && (
        <section className="space-y-2">
          {dash.alerts.map((a, i) => (
            <div key={i} className="card-elevated p-4 flex items-center gap-3" data-testid={`vault-alert-${a.kind}`}>
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <div className="text-sm">{a.label}</div>
            </div>
          ))}
        </section>
      )}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-elevated overflow-x-auto">
          <div className="px-6 py-3 border-b bg-secondary/40 font-heading">Récemment ajoutés</div>
          <ul className="divide-y">
            {dash.recent.length === 0 && <li className="p-4 text-sm text-muted-foreground">Aucun document récent.</li>}
            {dash.recent.map((d) => (
              <li key={d.id} className="p-4 flex items-center justify-between">
                <div className="text-sm"><div className="font-medium">{d.title}</div><div className="text-xs text-muted-foreground">{d.doc_type} · v{d.current_version}</div></div>
                <span className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleDateString("fr-FR")}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="card-elevated overflow-x-auto">
          <div className="px-6 py-3 border-b bg-secondary/40 font-heading">Répartition par type</div>
          <div className="p-4 space-y-2">
            {dash.by_type.map((b) => (
              <div key={b.doc_type} className="flex justify-between text-sm"><span>{b.doc_type}</span><span className="font-mono">{b.count}</span></div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------------- Upload dialog (shared) ---------------- */
function UploadDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState({ document_types: [], link_kinds: [] });
  const [links, setLinks] = useState({ buildings: [], lots: [], leases: [], tenants: [], companies: [], credits: [] });
  const [form, setForm] = useState({
    title: "", doc_type: "libre", description: "", tags: "",
    document_date: "", expires_at: "", link_kind: "", link_id: "", notes: "",
  });
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/vault/meta").then(({ data }) => setMeta(data));
    Promise.all([
      api.get("/buildings"), api.get("/lots"), api.get("/leases"),
      api.get("/tenants"), api.get("/companies"), api.get("/credits"),
    ]).then(([b, lo, le, t, c, cr]) => setLinks({
      buildings: b.data.items || [], lots: lo.data.items || [],
      leases: le.data.items || [], tenants: t.data.items || [],
      companies: c.data.items || [], credits: cr.data.items || [],
    }));
  }, [open]);

  const submit = async () => {
    if (!file || !form.title) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const payload = {
          title: form.title, doc_type: form.doc_type,
          description: form.description || undefined,
          tags: form.tags ? form.tags.split(",").map(s => s.trim()).filter(Boolean) : [],
          document_date: form.document_date || undefined,
          expires_at: form.expires_at || undefined,
          notes: form.notes || undefined,
          filename: file.name, mime: file.type,
          content_b64: reader.result,
          links: form.link_kind && form.link_id ? [{ kind: form.link_kind, id: form.link_id }] : [],
        };
        await api.post("/vault", payload);
        toast.success("Document ajouté au coffre");
        setOpen(false); onCreated?.();
      } catch (e) { toast.error(e.response?.data?.detail || "Erreur upload"); }
    };
    reader.readAsDataURL(file);
  };

  const linkOptions = () => ({
    building: links.buildings.map(x => ({ id: x.id, label: x.name })),
    lot: links.lots.map(x => ({ id: x.id, label: x.reference })),
    lease: links.leases.map(x => ({ id: x.id, label: `${x.lot_reference} — ${(x.tenant_names || []).join(", ")}` })),
    tenant: links.tenants.map(x => ({ id: x.id, label: x.display_name })),
    company: links.companies.map(x => ({ id: x.id, label: x.denomination || x.name })),
    credit: links.credits.map(x => ({ id: x.id, label: x.label || x.bank })),
  }[form.link_kind] || []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="btn-pill" data-testid="vault-upload-btn"><Upload className="mr-2 h-4 w-4" />Nouveau document</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Nouveau document</DialogTitle>
          <DialogDescription>Métadonnées + rattachement + fichier (PDF, image, ou autre).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
          <div><Label>Titre *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="up-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger data-testid="up-type"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.document_types.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Rattacher à</Label>
              <Select value={form.link_kind} onValueChange={(v) => setForm({ ...form, link_kind: v, link_id: "" })}>
                <SelectTrigger data-testid="up-linkkind"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>{["building", "lot", "lease", "tenant", "company", "credit"].map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.link_kind && (
              <div className="col-span-2"><Label>Objet lié</Label>
                <Select value={form.link_id} onValueChange={(v) => setForm({ ...form, link_id: v })}>
                  <SelectTrigger data-testid="up-linkid"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{linkOptions().map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Date document</Label><Input type="date" value={form.document_date} onChange={(e) => setForm({ ...form, document_date: e.target.value })} /></div>
            <div><Label>Expire le</Label><Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /></div>
            <div className="col-span-2"><Label>Tags (séparés par virgule)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="assurance, 2026, obligatoire" /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="col-span-2"><Label>Fichier *</Label>
              <Input type="file" accept="application/pdf,image/*" capture="environment" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="up-file" />
              {file && <div className="text-xs text-muted-foreground mt-1">{file.name} — {Math.round(file.size / 1024)} Ko</div>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
          <Button onClick={submit} disabled={!file || !form.title} data-testid="up-submit">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- List ---------------- */
function ListTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => api.get("/vault").then(({ data }) => { setItems(data.items || []); setTotal(data.total); }), []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => { const { data } = await api.get(`/vault/${id}`); setDetail(data); };
  const trashDoc = async (id) => { if (!window.confirm("Mettre à la corbeille ?")) return; await api.delete(`/vault/${id}`); toast.success("À la corbeille"); load(); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between"><div className="text-sm text-muted-foreground">{total} document(s) dans le coffre</div><UploadDialog onCreated={load} /></div>
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr className="text-left"><th className="px-6 py-3">Titre</th><th className="px-6 py-3">Type</th><th className="px-6 py-3">Version</th><th className="px-6 py-3">Ajouté</th><th className="px-6 py-3">Taille</th><th></th></tr>
          </thead>
          <tbody className="divide-y">
            {items.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground"><Files className="mx-auto h-8 w-8 mb-3 opacity-60" />Aucun document. Cliquez sur « Nouveau document ».</td></tr>}
            {items.map((d) => (
              <tr key={d.id} className="hover:bg-secondary/30" data-testid={`vdoc-${d.id}`}>
                <td className="px-6 py-3"><div className="font-medium">{d.title}</div><div className="text-xs text-muted-foreground">{d.filename}</div></td>
                <td className="px-6 py-3 text-xs">{d.doc_type}</td>
                <td className="px-6 py-3 font-mono">v{d.current_version}</td>
                <td className="px-6 py-3 text-xs text-muted-foreground">{new Date(d.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="px-6 py-3 font-mono text-xs">{Math.round((d.size_bytes || 0) / 1024)} Ko</td>
                <td className="px-6 py-3 text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openDetail(d.id)} data-testid={`open-${d.id}`}><Eye className="h-3 w-3" /></Button>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/vault/${d.id}/download`} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="ghost"><Download className="h-3 w-3" /></Button></a>
                  <Button size="sm" variant="ghost" onClick={() => trashDoc(d.id)}><Trash2 className="h-3 w-3" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DetailDialog detail={detail} onClose={() => setDetail(null)} onRefresh={load} />
    </div>
  );
}

function DetailDialog({ detail, onClose, onRefresh }) {
  const [shareForm, setShareForm] = useState({ expires_in_hours: 72, max_uses: "", password: "", allow_download: true });
  const [share, setShare] = useState(null);
  const [file, setFile] = useState(null);
  const [comment, setComment] = useState("");

  if (!detail) return null;

  const upNew = async () => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/vault/${detail.id}/versions`, {
          filename: file.name, mime: file.type, content_b64: reader.result, comment,
        });
        toast.success("Nouvelle version enregistrée");
        onRefresh(); onClose();
      } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };
    reader.readAsDataURL(file);
  };

  const doShare = async () => {
    try {
      const payload = {
        expires_in_hours: parseInt(shareForm.expires_in_hours),
        max_uses: shareForm.max_uses ? parseInt(shareForm.max_uses) : undefined,
        password: shareForm.password || undefined,
        allow_download: shareForm.allow_download,
      };
      const { data } = await api.post(`/vault/${detail.id}/share`, payload);
      setShare(data);
      toast.success("Lien de partage créé");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const restoreVersion = async (vid) => {
    if (!window.confirm("Restaurer cette version ?")) return;
    await api.post(`/vault/${detail.id}/versions/${vid}/restore`);
    toast.success("Version restaurée"); onRefresh(); onClose();
  };

  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>{detail.title}</DialogTitle>
          <DialogDescription>{detail.doc_type} · {detail.filename} · v{detail.current_version} · {Math.round((detail.size_bytes || 0) / 1024)} Ko</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-2 text-sm">
          {detail.description && <div className="text-muted-foreground">{detail.description}</div>}
          {(detail.tags || []).length > 0 && (<div className="flex flex-wrap gap-1">{detail.tags.map(t => <span key={t} className="text-xs px-2 py-1 rounded-full bg-secondary">{t}</span>)}</div>)}
          {(detail.links || []).length > 0 && (
            <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Rattachements</div>
              <div className="flex flex-wrap gap-1">{detail.links.map((l, i) => <span key={i} className="text-xs px-2 py-1 rounded-full bg-primary/10">{l.kind} · {l.id.slice(0, 8)}</span>)}</div>
            </div>
          )}
          {/* Versions */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Historique des versions</div>
            <div className="space-y-2">
              {(detail.version_history || []).map((v) => (
                <div key={v.id} className="rounded-xl border p-3 flex justify-between items-center">
                  <div><div className="font-medium">v{v.version} — {v.filename}</div><div className="text-xs text-muted-foreground">{new Date(v.created_at).toLocaleDateString("fr-FR")} · {Math.round((v.size_bytes || 0) / 1024)} Ko {v.comment ? `· "${v.comment}"` : ""}</div></div>
                  {!v.is_current && <Button size="sm" variant="ghost" onClick={() => restoreVersion(v.id)} data-testid={`restore-${v.id}`}><RotateCcw className="h-3 w-3 mr-1" />Restaurer</Button>}
                  {v.is_current && <StatusBadge tone="success">Actuelle</StatusBadge>}
                </div>
              ))}
            </div>
          </div>
          {/* New version */}
          <div className="rounded-xl border p-3">
            <div className="font-heading text-sm mb-2">Ajouter une nouvelle version</div>
            <div className="space-y-2">
              <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="newver-file" />
              <Input placeholder="Commentaire (optionnel)" value={comment} onChange={(e) => setComment(e.target.value)} />
              <Button size="sm" onClick={upNew} disabled={!file} data-testid="newver-submit"><Upload className="mr-2 h-3 w-3" />Uploader</Button>
            </div>
          </div>
          {/* Share */}
          <div className="rounded-xl border p-3">
            <div className="font-heading text-sm mb-2"><Share2 className="inline mr-1 h-3 w-3" />Lien de partage sécurisé</div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Expiration (h)</Label><Input type="number" value={shareForm.expires_in_hours} onChange={(e) => setShareForm({ ...shareForm, expires_in_hours: e.target.value })} data-testid="share-hours" /></div>
              <div><Label className="text-xs">Max consultations</Label><Input type="number" value={shareForm.max_uses} onChange={(e) => setShareForm({ ...shareForm, max_uses: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-xs">Mot de passe (optionnel)</Label><Input type="password" value={shareForm.password} onChange={(e) => setShareForm({ ...shareForm, password: e.target.value })} /></div>
              <label className="col-span-2 text-xs flex items-center gap-2"><input type="checkbox" checked={shareForm.allow_download} onChange={(e) => setShareForm({ ...shareForm, allow_download: e.target.checked })} /> Autoriser le téléchargement</label>
              <Button size="sm" className="col-span-2" onClick={doShare} data-testid="share-create"><Share2 className="mr-2 h-3 w-3" />Générer le lien</Button>
            </div>
            {share && (<div className="mt-3 rounded-xl bg-emerald-50 p-3 text-xs break-all">
              <div className="font-medium mb-1">Lien créé — expire {new Date(share.expires_at).toLocaleString("fr-FR")}</div>
              <a href={share.share_url} className="text-emerald-800 underline" target="_blank" rel="noopener noreferrer" data-testid="share-url">{share.share_url}</a>
            </div>)}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Search ---------------- */
function SearchTab() {
  const [q, setQ] = useState("");
  const [docType, setDocType] = useState("__all");
  const [meta, setMeta] = useState({ document_types: [] });
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/vault/meta").then(({ data }) => setMeta(data)); }, []);
  const run = async () => {
    const params = {};
    if (q) params.q = q;
    if (docType && docType !== "__all") params.doc_type = docType;
    const { data } = await api.get("/vault", { params });
    setItems(data.items || []);
  };
  return (
    <div className="space-y-4">
      <div className="card-elevated p-5 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div className="md:col-span-2"><Label>Recherche libre</Label><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="titre, tag, description, OCR…" data-testid="search-q" /></div>
        <div><Label>Type</Label>
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger data-testid="search-type"><SelectValue placeholder="Tous" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">Tous</SelectItem>{meta.document_types.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={run} data-testid="search-run"><Search className="mr-2 h-4 w-4" />Rechercher</Button>
      </div>
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground"><tr className="text-left"><th className="px-6 py-3">Titre</th><th className="px-6 py-3">Type</th><th className="px-6 py-3">Version</th><th></th></tr></thead>
          <tbody className="divide-y">
            {items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">Lancez une recherche pour afficher les résultats.</td></tr>}
            {items.map((d) => (
              <tr key={d.id}><td className="px-6 py-3">{d.title}</td><td className="px-6 py-3">{d.doc_type}</td><td className="px-6 py-3 font-mono">v{d.current_version}</td>
                <td className="px-6 py-3 text-right"><a href={`${process.env.REACT_APP_BACKEND_URL}/api/vault/${d.id}/download`} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline">Voir</Button></a></td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Trash ---------------- */
function TrashTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/vault?trash=true").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);
  const restore = async (id) => { await api.post(`/vault/${id}/restore`); toast.success("Restauré"); load(); };
  const purge = async (id) => { if (!window.confirm("Supprimer définitivement ? Cette action est irréversible.")) return; await api.delete(`/vault/${id}/purge`); toast.success("Supprimé définitivement"); load(); };
  return (
    <div className="card-elevated overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground"><tr className="text-left"><th className="px-6 py-3">Titre</th><th className="px-6 py-3">Purge prévue</th><th></th></tr></thead>
        <tbody className="divide-y">
          {items.length === 0 && <tr><td colSpan={3} className="p-12 text-center text-muted-foreground">Corbeille vide.</td></tr>}
          {items.map((d) => (
            <tr key={d.id} data-testid={`trash-${d.id}`}>
              <td className="px-6 py-3">{d.title}</td>
              <td className="px-6 py-3 text-xs text-muted-foreground">{d.purge_scheduled_at && new Date(d.purge_scheduled_at).toLocaleDateString("fr-FR")}</td>
              <td className="px-6 py-3 text-right space-x-1">
                <Button size="sm" variant="outline" onClick={() => restore(d.id)} data-testid={`restore-${d.id}`}><RotateCcw className="h-3 w-3 mr-1" />Restaurer</Button>
                <Button size="sm" variant="destructive" onClick={() => purge(d.id)} data-testid={`purge-${d.id}`}><Trash2 className="h-3 w-3 mr-1" />Purger</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
